import { useState, useCallback } from 'react';
import { DragStartEvent, DragEndEvent, DragOverEvent, DragMoveEvent } from '@dnd-kit/core';
import {
  EventLocationType,
  EventStatus,
  EventType,
} from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import dayjs from 'dayjs';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { formatDate } from '@frontend/date';
import { formatDateTime } from '@frontend/schedule-helpers';
import { parseColumnId } from '../../components/calendar-view/calendar-view-v3/helper';
import { CalendarViewOffices, GridCalendarEvent, ProcessedEventData } from '../../components/calendar-view/types';
import { useZoomSafeContext } from '../../components/calendar-view/zoom/zoom-context';
import { DATETIME_FORMATS } from '../../constants';
import { getTimeDiff } from '../../utils/date-helpers';
import { useAppointmentDragDropPendoTracking } from './use-appointment-drag-drop-pendo-tracking';

export const SNAP_INCREMENT_MULTIPLIER = 5;

export const findEventById = (data: CalendarViewOffices | undefined, eventId: string) => {
  if (!data) return;

  for (const office of data) {
    for (const entity of office.columnEntities ?? []) {
      const event = entity.events?.find((e) => e.eventId === eventId);
      if (event) return event as ProcessedEventData;
    }
  }

  return;
};

/**
 * Finds a GridCalendarEvent by its id from a flat GridCalendarEvent array
 * (the grid-based alternative to `findEventById` which searches CalendarViewOffices).
 */
export const findGridEventById = (events: GridCalendarEvent[], eventId: string): GridCalendarEvent | undefined =>
  events.find((e) => e.id === eventId);

/**
 * Gets the timezone string for a location from the SchedulerV3 locationTimezoneMap.
 * Grid-based alternative to `getLocationTimezone` which reads from CalendarViewOffices.
 */
export const getTimezoneFromLocationMap = (
  locationTimezoneMap: Record<string, { timezone: string } | undefined>,
  locationId?: string
): string => {
  if (!locationId) return '';
  return locationTimezoneMap[locationId]?.timezone ?? '';
};

const snapToIncrement = (px: number, pixelsPerMinute: number) => {
  const snapIncrementPx = SNAP_INCREMENT_MULTIPLIER * pixelsPerMinute;
  return Math.round(px / snapIncrementPx) * snapIncrementPx;
};

export const formatEventDateTimeForTracking = (
  dateStr?: string,
  timeStr?: string,
  fromTimezone?: string,
  toTimezone?: string
): string => {
  if (!dateStr || !timeStr) return '';

  return formatDateTime(`${dateStr} ${timeStr}`, {
    fromTimezone: fromTimezone ?? 'UTC',
    toTimezone: toTimezone ?? fromTimezone ?? 'UTC',
    outputFormat: DATETIME_FORMATS.ISO_DATETIME_WITH_OFFSET,
  });
};

export const getLocationTimezone = (data?: CalendarViewOffices, locationId?: string) => {
  if (!data) return '';
  return data?.find((office) => office.locationId === locationId)?.officeTimezone;
};

export const calculateNewDateTime = (
  calendarDate: string,
  newStartTimeMinutes: number,
  durationMinutes: number,
  calendarStartHour: number,
  timezone?: string
) => {
  const totalMinutes = newStartTimeMinutes + calendarStartHour * 60;

  const startDateObj = timezone
    ? dayjs.tz(calendarDate, DATETIME_FORMATS.US_DATE, timezone).startOf('day').add(totalMinutes, 'minute')
    : dayjs(calendarDate, DATETIME_FORMATS.US_DATE).startOf('day').add(totalMinutes, 'minute');
  const endDateObj = startDateObj.add(durationMinutes, 'minute');
  const startUtc = startDateObj.utc();
  const endUtc = endDateObj.utc();

  return {
    startDate: startUtc.format(DATETIME_FORMATS.ISO_DATE),
    startTime: startUtc.format(DATETIME_FORMATS.ISO_TIME),
    endDate: endUtc.format(DATETIME_FORMATS.ISO_DATE),
    endTime: endUtc.format(DATETIME_FORMATS.ISO_TIME),
  };
};

export type DraggedEventPosition = {
  top: number;
  height: number;
  width: string;
  left: string;
};

export type DropPayload = {
  eventId: string;
  sourceColumnEntityId: string;
  sourceLocationId: string;
  targetColumnEntityId: string;
  targetLocationId: string;
  newStartTimeMinutes: number;
};

export type DragDropState = {
  isDragging: boolean;
  activeEventId: string | null;
  sourceColumnId: string | null;
  overColumnId: string | null;
  draggedEventPosition: DraggedEventPosition | null;
  dropTargetTop: number | null;
  pendingDropPayload: DropPayload | null;
};

type UseAppointmentDragDropParams = {
  data?: CalendarViewOffices;
  calendarDate: string;
  startHour?: number;
  onDropSuccess?: () => void;
  onDropError?: (error: unknown) => void;
};

const initialDragDropState: DragDropState = {
  isDragging: false,
  activeEventId: null,
  sourceColumnId: null,
  overColumnId: null,
  draggedEventPosition: null,
  dropTargetTop: null,
  pendingDropPayload: null,
};

export const useAppointmentDragDrop = ({
  data,
  calendarDate,
  startHour = 0,
  onDropSuccess,
  onDropError,
}: UseAppointmentDragDropParams) => {
  const { pixelsPerMinute } = useZoomSafeContext();
  const [dragDropState, setDragDropState] = useState<DragDropState>(initialDragDropState);
  const trackPendoEvent = useAppointmentDragDropPendoTracking(data);

  const {
    invalidateListCalendarEvents,
    invalidateListCalendarEventsByAttendee,
    invalidateListScheduleActivity,
    invalidateListScheduleActivityHistory,
  } = SchedulerV3.Helpers.useSchedulerV3InvalidateQueries();

  const updateCalendarEventMutation = SchedulerV3.Mutations.useUpdateCalendarEventMutation();

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const { active } = event;
      const sourceColumnId = active.data?.current?.sourceColumnId ?? null;
      const draggedEventPosition = active.data?.current?.eventPosition ?? null;
      const eventId = String(active.id);

      setDragDropState((prev) => ({
        ...prev,
        isDragging: true,
        activeEventId: eventId,
        sourceColumnId,
        draggedEventPosition,
        dropTargetTop: draggedEventPosition?.top ?? null,
      }));

      trackPendoEvent('appointment_card_dragged', { eventId, sourceColumnId });
    },
    [trackPendoEvent]
  );

  const handleDragMove = useCallback(
    (event: DragMoveEvent) => {
      const { delta, active } = event;
      const draggedEventPosition = active.data?.current?.eventPosition;

      if (draggedEventPosition) {
        const rawTop = draggedEventPosition.top + delta.y;
        const snappedTop = Math.max(0, snapToIncrement(rawTop, pixelsPerMinute));
        setDragDropState((prev) => ({ ...prev, dropTargetTop: snappedTop }));
      }
    },
    [pixelsPerMinute]
  );

  const handleDragOver = useCallback((event: DragOverEvent) => {
    const { over } = event;
    setDragDropState((prev) => ({ ...prev, overColumnId: over ? String(over.id) : null }));
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      // If dropped on invalid target
      if (!over) {
        setDragDropState(initialDragDropState);
      }

      const { locationId: targetLocationId, entityId: targetColumnEntityId } = parseColumnId(String(over?.id));
      const sourceColumnId = active.data?.current?.sourceColumnId;
      const { locationId: sourceLocationId, entityId: sourceColumnEntityId } = parseColumnId(sourceColumnId);
      const draggedEventPosition = active.data?.current?.eventPosition;

      if (!targetLocationId || targetColumnEntityId === undefined) {
        setDragDropState(initialDragDropState);
        return;
      }

      // Calculate new start time in minutes based on drop position, snapped to 5-minute increments
      const rawPosition = draggedEventPosition ? draggedEventPosition.top + (event.delta?.y || 0) : 0;
      const snappedPosition = Math.max(0, snapToIncrement(rawPosition, pixelsPerMinute));
      const newStartTimeMinutes = snappedPosition / pixelsPerMinute;

      const isDroppedOutsideCalendarDay = newStartTimeMinutes > 1439; // 1440 minutes in a day

      // Check if position and provider haven't changed
      const originalStartTimeMinutes = draggedEventPosition ? draggedEventPosition.top / pixelsPerMinute : 0;
      const isSamePosition = Math.abs(newStartTimeMinutes - originalStartTimeMinutes) < 1; // Less than 1 minute difference
      const isSameProvider = sourceColumnEntityId === targetColumnEntityId;

      // Reset state without showing modal if nothing has changed OR dropped outside current calendar day
      if ((isSamePosition && isSameProvider) || isDroppedOutsideCalendarDay) {
        setDragDropState(initialDragDropState);
        return;
      }

      const dropPayload: DropPayload = {
        eventId: String(active.id),
        sourceColumnEntityId: sourceColumnEntityId || '',
        sourceLocationId: sourceLocationId || '',
        targetColumnEntityId,
        targetLocationId,
        newStartTimeMinutes,
      };

      setDragDropState((prev) => ({
        ...prev,
        isDragging: false,
        pendingDropPayload: dropPayload,
      }));
    },
    [pixelsPerMinute]
  );

  const handleDragCancel = useCallback(() => {
    setDragDropState(initialDragDropState);
  }, []);

  const cancelDrop = useCallback(
    (event?: React.MouseEvent<HTMLButtonElement>) => {
      // Only track when explicitly triggered by button click, not modal backdrop or ESC key dismissal
      if (!event) return;

      const { activeEventId, sourceColumnId, pendingDropPayload } = dragDropState;

      if (activeEventId && sourceColumnId && pendingDropPayload) {
        const originalEvent = findEventById(data, activeEventId);
        const { entityId: sourceColumnEntityId } = parseColumnId(sourceColumnId);
        const { targetColumnEntityId, targetLocationId, newStartTimeMinutes } = pendingDropPayload;
        const locationTimezone = getLocationTimezone(data, targetLocationId);

        if (originalEvent) {
          const durationMinutes = getTimeDiff(originalEvent.startHour, originalEvent.endHour, {
            format: 'hh:mm A',
            crossDay: true,
          });

          const { startDate, startTime, endDate, endTime } = calculateNewDateTime(
            calendarDate,
            newStartTimeMinutes,
            durationMinutes,
            startHour,
            locationTimezone
          );

          const newStartDateTime = formatEventDateTimeForTracking(startDate, startTime, 'UTC', locationTimezone);
          const newEndDateTime = formatEventDateTimeForTracking(endDate, endTime, 'UTC', locationTimezone);

          trackPendoEvent('appointment_card_drag_cancel_button_clicked', {
            eventId: activeEventId,
            sourceColumnEntityId,
            targetColumnEntityId,
            newStartDateTime,
            newEndDateTime,
          });
        }
      }

      setDragDropState(initialDragDropState);
    },
    [dragDropState, data, calendarDate, startHour, trackPendoEvent]
  );

  const confirmDrop = useCallback(async () => {
    const { pendingDropPayload } = dragDropState;

    if (!pendingDropPayload) {
      setDragDropState(initialDragDropState);
      return;
    }

    const { eventId, targetColumnEntityId, newStartTimeMinutes, targetLocationId, sourceColumnEntityId } =
      pendingDropPayload;
    const locationTimezone = getLocationTimezone(data, targetLocationId);

    const originalEvent = findEventById(data, eventId);

    if (!originalEvent) {
      console.error('Could not find original event for drag-drop update');
      setDragDropState(initialDragDropState);
      return;
    }

    const targetEntity = data
      ?.flatMap((office) => office.columnEntities || [])
      .find((entity) => entity.entityId === targetColumnEntityId);

    const durationMinutes = getTimeDiff(originalEvent.startHour, originalEvent.endHour, {
      format: 'hh:mm A',
      crossDay: true,
    });

    const { startDate, startTime, endDate, endTime } = calculateNewDateTime(
      calendarDate,
      newStartTimeMinutes,
      durationMinutes,
      startHour,
      locationTimezone
    );

    const newStartDateTime = formatEventDateTimeForTracking(startDate, startTime, 'UTC', locationTimezone);
    const newEndDateTime = formatEventDateTimeForTracking(endDate, endTime, 'UTC', locationTimezone);

    trackPendoEvent('appointment_card_drag_confirm_button_clicked', {
      eventId,
      sourceColumnEntityId,
      targetColumnEntityId,
      newStartDateTime,
      newEndDateTime,
    });

    try {
      const payload = {
        eventId,
        attendeeId: originalEvent.patientId || '',
        attendeeStatus: originalEvent.status || EventStatus.UNCONFIRMED,
        eventType: EventType.APPOINTMENT,
        locationType: EventLocationType.OFFICE,
        organizerId: targetColumnEntityId,
        organizerCalendarId: targetEntity?.calendarId || '',
        startDate,
        startTime,
        endDate,
        endTime,
        startTimezone: locationTimezone || '',
        endTimezone: locationTimezone || '',
        referenceId: originalEvent.referenceId || '',
        referenceTypeId: originalEvent.appointmentTypeId || '',
        details: {
          dur: durationMinutes,
          orNa: targetEntity?.entityName || '',
          rTyNa: originalEvent.type,
          col: originalEvent.backgroundColor || originalEvent.displayOptions?.backgroundColor || '',
        },
        existingStartDate: formatDate(originalEvent.startDate, DATETIME_FORMATS.ISO_DATE),
      };

      await updateCalendarEventMutation.mutateAsync(payload);
      invalidateListCalendarEvents({
        locationId: targetLocationId,
        ignoreDate: true,
      });
      invalidateListCalendarEventsByAttendee(targetLocationId);
      invalidateListScheduleActivity({ locationId: targetLocationId });
      invalidateListScheduleActivityHistory({ locationId: targetLocationId, referenceId: payload.referenceId });

      onDropSuccess?.();
    } catch (error) {
      console.error('Failed to update calendar event:', error);
      onDropError?.(error);
    } finally {
      setDragDropState(initialDragDropState);
    }
  }, [
    dragDropState,
    data,
    calendarDate,
    startHour,
    updateCalendarEventMutation,
    invalidateListCalendarEvents,
    invalidateListCalendarEventsByAttendee,
    invalidateListScheduleActivity,
    invalidateListScheduleActivityHistory,
    onDropSuccess,
    onDropError,
    trackPendoEvent,
  ]);

  return {
    dragDropState,
    handleDragStart,
    handleDragMove,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
    confirmDrop,
    cancelDrop,
  };
};
